import { useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { Bold, Italic, Heading, List, Eye, Pencil } from 'lucide-react';

/**
 * Lightweight Markdown editor built on the shadcn textarea. Ships a
 * tiny inline toolbar that wraps the current selection with **bold**,
 * *italic*, headings and bulleted lists, plus a live preview toggle
 * so admins can format storefront product descriptions without
 * hand-writing raw Markdown.
 *
 * Storage stays as plain Markdown — no schema changes needed. The
 * storefront dialog renders it with `MarkdownDescription`.
 */
export function MarkdownEditor({ value, onChange, placeholder, rows = 5, testId }) {
  const [mode, setMode] = useState('write'); // 'write' | 'preview'
  const ref = useRef(null);

  const wrap = (left, right = left, sample = '') => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const text = value || '';
    const selected = text.slice(start, end) || sample;
    const next = text.slice(0, start) + left + selected + right + text.slice(end);
    onChange?.(next);
    // Restore selection just after the inserted block so the user can
    // keep typing without losing focus.
    requestAnimationFrame(() => {
      el.focus();
      const caret = start + left.length + selected.length;
      el.setSelectionRange(caret, caret);
    });
  };

  const linePrefix = (prefix) => {
    const el = ref.current;
    if (!el) return;
    const text = value || '';
    const start = el.selectionStart ?? 0;
    const lineStart = text.lastIndexOf('\n', start - 1) + 1;
    const before = text.slice(0, lineStart);
    const after = text.slice(lineStart);
    const next = `${before}${prefix}${after}`;
    onChange?.(next);
    requestAnimationFrame(() => {
      el.focus();
      const caret = start + prefix.length;
      el.setSelectionRange(caret, caret);
    });
  };

  return (
    <div className="border border-slate-200 rounded-md bg-white" data-testid={testId}>
      <div className="flex items-center justify-between px-2 py-1.5 border-b border-slate-100 bg-slate-50">
        <div className="flex items-center gap-0.5">
          <ToolbarBtn onClick={() => wrap('**', '**', 'bold')} title="Bold (Ctrl+B)" testId={`${testId}-bold`}>
            <Bold size={13} />
          </ToolbarBtn>
          <ToolbarBtn onClick={() => wrap('*', '*', 'italic')} title="Italic" testId={`${testId}-italic`}>
            <Italic size={13} />
          </ToolbarBtn>
          <span className="w-px h-4 bg-slate-200 mx-1" />
          <ToolbarBtn onClick={() => linePrefix('## ')} title="Heading" testId={`${testId}-heading`}>
            <Heading size={13} />
          </ToolbarBtn>
          <ToolbarBtn onClick={() => linePrefix('- ')} title="Bulleted list" testId={`${testId}-list`}>
            <List size={13} />
          </ToolbarBtn>
        </div>
        <div className="flex items-center gap-1">
          <ToolbarBtn
            onClick={() => setMode('write')}
            title="Write"
            active={mode === 'write'}
            testId={`${testId}-mode-write`}
          >
            <Pencil size={12} />
          </ToolbarBtn>
          <ToolbarBtn
            onClick={() => setMode('preview')}
            title="Preview"
            active={mode === 'preview'}
            testId={`${testId}-mode-preview`}
          >
            <Eye size={12} />
          </ToolbarBtn>
        </div>
      </div>
      {mode === 'write' ? (
        <textarea
          ref={ref}
          value={value || ''}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder || 'Add a description — **bold**, *italic*, - bullets, ## headings supported.'}
          rows={rows}
          className="w-full px-3 py-2 text-sm resize-y focus:outline-none rounded-b-md font-mono text-slate-800"
          data-testid={`${testId}-textarea`}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'b') {
              e.preventDefault();
              wrap('**', '**', 'bold');
            }
          }}
        />
      ) : (
        <div className="px-3 py-3 min-h-[8rem]" data-testid={`${testId}-preview`}>
          {value ? (
            <MarkdownDescription source={value} />
          ) : (
            <p className="text-xs text-slate-400 italic">Nothing to preview yet.</p>
          )}
        </div>
      )}
    </div>
  );
}

const MD_COMPONENTS = {
  h1: (p) => <h3 className="text-sm font-semibold text-slate-900 mt-2 mb-1" {...p} />,
  h2: (p) => <h4 className="text-sm font-semibold text-slate-900 mt-2 mb-1" {...p} />,
  h3: (p) => <h5 className="text-xs font-semibold text-slate-800 mt-2 mb-1" {...p} />,
  p:  (p) => <p className="text-sm text-slate-700 my-1.5" {...p} />,
  ul: (p) => <ul className="list-disc pl-5 space-y-0.5 my-1.5 text-sm text-slate-700" {...p} />,
  ol: (p) => <ol className="list-decimal pl-5 space-y-0.5 my-1.5 text-sm text-slate-700" {...p} />,
  li: (p) => <li className="text-sm" {...p} />,
  strong: (p) => <strong className="font-semibold text-slate-900" {...p} />,
  em: (p) => <em className="italic text-slate-800" {...p} />,
  a:  (p) => <a className="text-emerald-700 underline underline-offset-2" target="_blank" rel="noreferrer" {...p} />,
};

/**
 * Read-only markdown renderer used by both the editor preview and the
 * storefront product dialog. Keeps the emitted DOM lightweight —
 * headings, bullets, bold/italic/links only.
 */
export function MarkdownDescription({ source, className = '' }) {
  return (
    <div className={`prose prose-sm max-w-none text-slate-700 ${className}`}>
      <ReactMarkdown components={MD_COMPONENTS}>{source || ''}</ReactMarkdown>
    </div>
  );
}

function ToolbarBtn({ children, onClick, title, active, testId }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center justify-center h-6 min-w-6 px-1.5 rounded text-slate-600 hover:text-slate-900 hover:bg-white transition ${
        active ? 'bg-white text-slate-900 shadow-sm border border-slate-200' : ''
      }`}
    >
      {children}
    </button>
  );
}
